import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import { Menu, Plus } from "lucide-react";
import astreonLogo from "@/assets/astreon-logo.png";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useState, useEffect, useRef } from "react";
import MegaMenuPanel, { megaMenus } from "@/components/MegaMenu";

interface NavItemWithDropdown {
  label: string;
  menuKey: string;
}

interface NavItemSimple {
  label: string;
  to: string;
}

type NavItem = NavItemWithDropdown | NavItemSimple;

const isDropdown = (item: NavItem): item is NavItemWithDropdown => "menuKey" in item;

const navItems: NavItem[] = [
  { label: "Products", menuKey: "Products" },
  { label: "Industries", menuKey: "Solutions" },
  { label: "Case studies", to: "/case-studies" },
  { label: "Case scenarios", to: "/case-scenarios" },
  { label: "Testimonials", to: "/testimonials" },
  { label: "Security", to: "/security" },
  
  { label: "About us", menuKey: "About us" },
  { label: "Insights", menuKey: "News" },
  { label: "Careers", to: "/careers" },
  { label: "Contact Us", to: "/contact-us" },
];

const mobileSubItems: Record<string, { to: string; label: string }[]> = {
  Products: [
    { to: "/product", label: "Product overview" },
    { to: "/products/voice", label: "Astreon Voice" },
    { to: "/products/flow", label: "Astreon Flow" },
    { to: "/products/insights", label: "Astreon Insights" },
    { to: "/products/engage", label: "Astreon Engage" },
    { to: "/products/custom", label: "Astreon Custom" },
    { to: "/clinic-genius-ai", label: "Clinic Genius AI" },
    { to: "/voice-agent-builder", label: "AI receptionist builder" },
    { to: "/clinarium", label: "Clinarium AI" },
  ],
  Industries: [
    { to: "/industries/construction", label: "Construction" },
    { to: "/industries/ecommerce", label: "E-commerce" },
    { to: "/industries/education", label: "Education" },
    { to: "/industries/finance", label: "Finance" },
    { to: "/industries/healthcare", label: "Healthcare" },
    { to: "/industries/hospitality", label: "Hospitality" },
    { to: "/industries/insurance", label: "Insurance" },
    { to: "/industries/it-enterprises", label: "IT and operational enterprises" },
    { to: "/industries/law-firms", label: "Law Firms" },
    { to: "/industries/logistics", label: "Logistics" },
    { to: "/industries/manufacturing", label: "Manufacturing" },
    { to: "/industries/real-estate", label: "Real estate" },
    { to: "/industries/recruitment", label: "Recruitment" },
    { to: "/industries/retail", label: "Retail" },
    { to: "/industries/travel-tourism", label: "Travel & Tourism" },
  ],
  "About us": [
    { to: "/about/who-we-are", label: "Who we are" },
    { to: "/about/leadership", label: "Our Leadership" },
    { to: "/about/team", label: "Meet the Team" },
    { to: "/about/purpose", label: "Purpose, Mission & Values" },
    { to: "/about/history", label: "Our History" },
    { to: "/about/aspiration", label: "Our Aspiration" },
    { to: "/about/governance", label: "Our Governance" },
  ],
  Insights: [
    { to: "/blog", label: "Blog" },
    { to: "/newsletters", label: "Newsletters" },
    { to: "/reels", label: "Film library" },
  ],
};

const Navigation = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const isHomepage = location.pathname === "/";

  useEffect(() => {
    let raf = 0;
    const handleScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(y > 20);
        setPastHero(y > 400);
        raf = 0;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const handleMouseEnter = (label: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setActiveDropdown(null), 150);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background ${scrolled ? "shadow-sm" : ""}`}>
      <div className="w-full px-3 sm:px-4 lg:px-3 h-[48px] sm:h-[52px] flex items-center justify-between">
        <Link to="/" className="flex items-center">
          {isHomepage && pastHero ? (
            <img src={astreonLogo} alt="Astreon AI Group" className="h-8 w-auto" />
          ) : (
            <span className="text-[24px] sm:text-[26px] font-extrabold tracking-[0.02em] uppercase text-foreground leading-none">
              ASTREON AI
            </span>
          )}
        </Link>

        {/* Mobile Menu */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="min-[1100px]:hidden">
            <Button variant="ghost" className="text-foreground h-8 px-2 text-[13px] font-medium tracking-wide uppercase">
              Menu
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="bg-background border-border overflow-y-auto w-[300px]">
            <SheetHeader>
              <SheetTitle className="text-foreground font-extrabold tracking-[0.04em] uppercase text-[20px]">
                ASTREON AI
              </SheetTitle>
            </SheetHeader>
            <div className="flex flex-col gap-0 mt-8">
              {navItems.map((item) =>
                isDropdown(item) ? (
                  <div key={item.label} className="border-b border-border">
                    <button
                      onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                      className="flex items-center justify-between w-full text-foreground py-3.5 text-[15px] text-body"
                    >
                      {item.label}
                      <Plus className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${mobileExpanded === item.label ? "rotate-45" : ""}`} />
                    </button>
                    {mobileExpanded === item.label && mobileSubItems[item.label] && (
                      <div className="pb-3 space-y-0">
                        {mobileSubItems[item.label].map((subItem) => (
                          <Link
                            key={subItem.label}
                            to={subItem.to}
                            className="block text-muted-foreground hover:text-foreground transition-colours py-2 pl-4 text-[14px] text-body"
                            onClick={() => setOpen(false)}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.label}
                    to={item.to}
                    className="text-foreground py-3.5 text-[15px] text-body border-b border-border"
                    onClick={() => setOpen(false)}
                  >
                    {item.label}
                  </Link>
                )
              )}
            </div>
            {(!isHomepage || pastHero) && (
              <Link
                to="/book-a-demo"
                className="mt-6 inline-flex items-center justify-center bg-foreground text-background text-[14px] text-body font-medium px-6 py-3 rounded-md hover:bg-foreground/90 transition-colours w-full text-center"
                onClick={() => setOpen(false)}
              >
                Book a complimentary audit
              </Link>
            )}
          </SheetContent>
        </Sheet>

        {/* Desktop Menu */}
        <div className="hidden min-[1100px]:flex items-center gap-0">
          {navItems.map((item) =>
            isDropdown(item) ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => handleMouseEnter(item.menuKey)}
                onMouseLeave={handleMouseLeave}
              >
                <button className={`flex items-center gap-1 text-foreground hover:text-foreground/60 transition-colors text-[12px] text-body px-2 py-2 leading-none whitespace-nowrap ${activeDropdown === item.menuKey ? 'underline underline-offset-4' : ''}`}>
                  {item.label}
                  <span className="text-[11px] leading-none">+</span>
                </button>
              </div>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                className="text-foreground hover:text-foreground/60 transition-colours text-[12px] text-body px-2 py-2 leading-none whitespace-nowrap"
              >
                {item.label}
              </Link>
            )
          )}

          {(!isHomepage || pastHero) && (
            <Link
              to="/book-a-demo"
              className="ml-2 inline-flex items-center justify-center bg-foreground text-background text-[12px] text-body font-medium px-4 py-2 rounded-md hover:bg-foreground/90 transition-colours leading-none whitespace-nowrap"
            >
              Book a complimentary audit
            </Link>
          )}
        </div>
      </div>

      <div className="h-px bg-border" />

      {/* Full-width mega menu panel */}
      {activeDropdown && megaMenus[activeDropdown] && (
        <div
          onMouseEnter={() => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
          }}
          onMouseLeave={handleMouseLeave}
        >
          <MegaMenuPanel
            config={megaMenus[activeDropdown]}
            onClose={() => setActiveDropdown(null)}
          />
        </div>
      )}
    </nav>
  );
};

export default Navigation;
